// Cookie utility functions for managing authentication and user data

/**
 * Set a cookie with optional expiration days
 * @param name Cookie name
 * @param value Cookie value
 * @param days Number of days until expiration (default: 7)
 * @param secure Use secure flag (default: true in production)
 * @param sameSite SameSite attribute (default: 'Lax')
 */
export function setCookie(
  name: string,
  value: string,
  days: number = 7,
  secure: boolean = window.location.protocol === 'https:',
  sameSite: 'Strict' | 'Lax' | 'None' = 'Lax'
): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  
  let cookieString = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=${sameSite}`;
  
  if (secure) {
    cookieString += ';Secure';
  }
  
  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  
  return null;
}

/**
 * Remove a cookie by name
 * @param name Cookie name
 */
export function removeCookie(name: string): void {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

/**
 * Check if a cookie exists
 * @param name Cookie name
 * @returns true if cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Get all cookies as an object
 * @returns Object with cookie names as keys
 */
export function getAllCookies(): Record<string, string> {
  const cookies: Record<string, string> = {};
  const cookieArray = document.cookie.split(';');
  
  for (const cookie of cookieArray) {
    const [name, value] = cookie.split('=').map(c => c.trim());
    if (name && value) {
      cookies[name] = decodeURIComponent(value);
    }
  }
  
  return cookies;
}

/**
 * Clear all cookies
 */
export function clearAllCookies(): void {
  const cookies = getAllCookies();
  Object.keys(cookies).forEach(name => removeCookie(name));
}
